import { SQLFilters } from '../../../api/types';
import { RegularMetricId } from '../../../api/types-graphql';
import {
  ChartGridConfig,
  LanguageString,
} from '../../../common/components/dashboards/types';
import {
  KpiFactboxBenchmarkPeriods,
  KpiFactboxBenchmarkTypes,
} from '../../../common/components/data-viz-containers/common/types';
import { KpiFactboxSingleMetricVizViews } from '../../../common/components/data-viz-containers/kpi-factbox-single-metric/types';
import { OverTimeUnsegmentedVizViews } from '../../../common/components/data-viz-containers/over-time-unsegmented/types';
import { TimePeriodUnsegmentedVizViews } from '../../../common/components/data-viz-containers/time-period-unsegmented/types';
import {
  StoryboardComponentIds,
  StoryboardItemConfig,
} from '../../../common/components/storyboard-components/common/types';
import { FactboxFontSizes } from '../../../common/components/ui-components/data-viz-ui/factboxes/common/types';
import { Languages } from '../../../constants';
import { DataFieldWithDataType, TimeSelectionType } from '../../../types';
import { getKeyFromDataFieldWithDataType } from '../../../utils';
import { StoryboardDateUtils } from '../utils';
import { getFactboxGridConfig, topLineChartGridConfig } from './chart-layouts';

export const getTopChartConfig = (
  storyboardDateUtils: StoryboardDateUtils,
  filters: SQLFilters | null,
  metricId: RegularMetricId,
  title: LanguageString
): StoryboardItemConfig => {
  const { twelveMonthsAgoFromNowEndOfMonth, nowEndOfMonth } =
    storyboardDateUtils;
  return {
    chartId: 'top-chart',
    layoutConfig: {
      ...topLineChartGridConfig,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.OVERTIME_UNSEGMENTED,
      dataConfig: {
        title,
        queries: [
          {
            metric: {
              type: 'RegularMetricId',
              value: metricId,
            },
            filters: filters ?? undefined,
          },
        ],
        timeSelection: {
          type: TimeSelectionType.CalendarYearMonthly,
          input: {
            start: twelveMonthsAgoFromNowEndOfMonth,
            end: nowEndOfMonth,
          },
        },
      },
      selectedView: OverTimeUnsegmentedVizViews.LINE_CHART,
    },
  };
};

export const getByDimensionChartConfig = (
  storyboardDateUtils: StoryboardDateUtils,
  dimension: DataFieldWithDataType,
  metricId: RegularMetricId,
  title: LanguageString,
  filters: SQLFilters | null,
  gridConfig: ChartGridConfig
): StoryboardItemConfig => {
  const { nowEndOfMonth, twelveMonthsAgoFromNowEndOfMonth } =
    storyboardDateUtils;
  return {
    chartId: `by-dimension-chart-${getKeyFromDataFieldWithDataType(dimension)}`,
    layoutConfig: {
      ...gridConfig,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.TIME_PERIOD_UNSEGMENTED,
      dataConfig: {
        title,
        queries: [
          {
            metric: {
              type: 'RegularMetricId',
              value: metricId,
            },
          },
        ],
        filters: filters ?? undefined,
        timeSelection: {
          type: TimeSelectionType.CalendarYearSingleValueByMonths,
          input: {
            start: twelveMonthsAgoFromNowEndOfMonth,
            end: nowEndOfMonth,
          },
        },
        groupBy: {
          type: 'non-hierarchical',
          dataField: dimension,
        },
      },
      selectedView: TimePeriodUnsegmentedVizViews.BAR_CHART,
    },
  };
};

export const getNikkiTTMFactbox = (
  storyboardDateUtils: StoryboardDateUtils,
  filters: SQLFilters | null
): StoryboardItemConfig => {
  const { nowEndOfMonth, twelveMonthsAgoFromNowEndOfMonth } =
    storyboardDateUtils;
  return {
    chartId: 'tenure-factbox',
    layoutConfig: {
      ...getFactboxGridConfig(2),
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: 'TTM(Trailing Twelve Months) %',
          [Languages.JA]: 'TTM(Trailing Twelve Months) %',
        },
        section: {
          query: {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0318_3CustomAttritionNikkiTtm,
            },
            timeSelection: {
              type: TimeSelectionType.CalendarYearSingleValueByMonths,
              input: {
                start: twelveMonthsAgoFromNowEndOfMonth,
                end: nowEndOfMonth,
              },
            },
            filters: filters ?? undefined,
          },
          benchmarkConfigs: [
            {
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
            },
          ],
        },
        fontSize: FactboxFontSizes.SMALL,
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  };
};

export const getNikkiLeaversFactbox = (
  storyboardDateUtils: StoryboardDateUtils,
  filters: SQLFilters | null
): StoryboardItemConfig => {
  const { nowEndOfMonth, twelveMonthsAgoFromNowEndOfMonth } =
    storyboardDateUtils;
  return {
    chartId: 'leavers-factbox',
    layoutConfig: {
      ...getFactboxGridConfig(2),
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: 'Leavers',
          [Languages.JA]: 'Leavers',
        },
        section: {
          query: {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0318_4CustomAttritionNikkiLeavers,
            },
            timeSelection: {
              type: TimeSelectionType.CalendarYearSingleValueByMonths,
              input: {
                start: twelveMonthsAgoFromNowEndOfMonth,
                end: nowEndOfMonth,
              },
            },
            filters: filters ?? undefined,
          },
          benchmarkConfigs: [
            {
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
            },
          ],
        },
        fontSize: FactboxFontSizes.SMALL,
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  };
};
